import React, { useState } from "react";
import { Container, Row, Col, Form, Alert } from "react-bootstrap";
import {
  FaArrowRight,
  FaCheck,
  FaClock,
  FaEnvelope,
  FaMapMarkerAlt,
  FaPaperPlane,
  FaPhone,
} from "react-icons/fa";

import api from "../api/axios";
import CustomNavbar from "../components/CustomNavbar";
import Footer from "../components/Footer";
import { services } from "../data/services";

const emptyForm = {
  name: "",
  company: "",
  email: "",
  phone: "",
  service: "",
  message: "",
};

const REASONS = [
  "Technology Transformation Experts",
  "Project & Programme Delivery Specialists",
  "Enterprise Integration Expertise",
  "Industry-Focused Consultancy",
];

const DETAILS = [
  { Icon: FaEnvelope, label: "Email", value: "info@sarvavya.com", href: "mailto:info@sarvavya.com" },
  { Icon: FaPhone, label: "Phone", value: "+44 XXX XXX XXXX", href: "tel:+44XXXXXXXXX" },
  { Icon: FaMapMarkerAlt, label: "Location", value: "United Kingdom" },
  { Icon: FaClock, label: "Business Hours", value: "Mon – Fri, 9:00 AM – 6:00 PM" },
];

function Contact() {
  const [formData, setFormData] = useState(emptyForm);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus({ type: "", message: "" });

    try {
      await api.post("/contact", formData);
      setStatus({
        type: "success",
        message: "Message submitted successfully! We'll be in touch soon.",
      });
      setFormData(emptyForm);
    } catch (err) {
      setStatus({
        type: "danger",
        message:
          err.response?.data?.message ||
          "Something went wrong. Please try again later.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <CustomNavbar />

      <style>{`
        .cnt {
          --peach-deep: #ff6b35;
          --peach: #ff9066;
          --peach-soft: #f3c09f;
          --peach-tint: #ffe9e0;
          --peach-ink: #c2410c;
        }

        .cnt-hero {
          position: relative; overflow: hidden;
          padding: clamp(52px, 7vw, 84px) 0;
          background:
            radial-gradient(42rem 24rem at 86% 6%, rgba(255,107,53,.30), transparent 62%),
            linear-gradient(135deg, #ff9066 0%, #f3c09f 48%, #fff8f5 100%);
          color: #0f172a;
        }
        .cnt-hero__blob {
          position: absolute; border-radius: 50%; pointer-events: none;
          filter: blur(80px); opacity: .5;
          width: 24rem; height: 24rem; bottom: -9rem; left: -8rem;
          background: radial-gradient(circle, #fff3ec, transparent 70%);
        }
        .cnt-badge {
          display: inline-flex; align-items: center; gap: 8px;
          background: #ffffffdd; color: var(--peach-ink);
          padding: 9px 22px; border-radius: 999px;
          font-weight: 700; font-size: .78rem; letter-spacing: .1em;
          box-shadow: 0 8px 20px rgba(194,65,12,.16);
        }
        .cnt-hero__title {
          margin-top: 22px; font-weight: 800; letter-spacing: -.02em;
          font-size: clamp(1.9rem, 4.6vw, 3.1rem); line-height: 1.14;
        }
        .cnt-hero__title em {
          font-style: normal;
          background: linear-gradient(96deg, #c2410c, #ff6b35 60%, #b91c1c);
          -webkit-background-clip: text; background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .cnt-hero__lead {
          max-width: 720px; margin: 18px auto 0; color: #4a3a33;
          font-size: clamp(1rem, 1.7vw, 1.08rem); line-height: 1.85;
        }

        /* Ends white so the footer's peach CTA panel reads against it */
        .cnt-body {
          padding: clamp(52px, 7vw, 84px) 0 clamp(92px, 10vw, 130px);
          background: linear-gradient(180deg, #fff8f5 0%, #ffffff 55%, #ffffff 100%);
        }

        /* --- Info panel: navy, so it contrasts the white form --- */
        .cnt-info {
          position: relative; overflow: hidden; height: 100%;
          border-radius: 24px; padding: clamp(26px, 3.2vw, 38px);
          color: #fff;
          background:
            radial-gradient(28rem 18rem at 88% 4%, rgba(255,107,53,.30), transparent 62%),
            linear-gradient(150deg, #131f36 0%, #0f172a 55%, #0b1120 100%);
          box-shadow: 0 24px 50px rgba(15,23,42,.3);
        }
        .cnt-info__h { font-weight: 800; font-size: 1.5rem; margin-bottom: 12px; }
        .cnt-info__p { color: #cbd5e1; line-height: 1.85; font-size: .95rem; margin-bottom: 26px; }
        .cnt-detail {
          display: flex; align-items: flex-start; gap: 14px;
          margin-bottom: 18px; text-decoration: none;
        }
        .cnt-detail__icon {
          width: 44px; height: 44px; border-radius: 13px; flex-shrink: 0;
          display: grid; place-items: center;
          background: rgba(255,144,102,.14);
          border: 1px solid rgba(255,144,102,.3);
          color: var(--peach);
          transition: background .24s ease, color .24s ease;
        }
        .cnt-detail__l {
          display: block; color: #94a3b8; font-size: .72rem;
          letter-spacing: .1em; text-transform: uppercase; font-weight: 700;
        }
        .cnt-detail__v { display: block; color: #e2e8f0; font-size: .96rem; margin-top: 2px; }
        a.cnt-detail:hover .cnt-detail__v { color: var(--peach); }
        a.cnt-detail:hover .cnt-detail__icon {
          background: linear-gradient(135deg, var(--peach-deep), var(--peach)); color: #fff;
        }
        .cnt-why {
          margin-top: 28px; padding: 20px;
          border-radius: 16px;
          background: rgba(255,255,255,.05);
          border: 1px solid rgba(255,144,102,.22);
        }
        .cnt-why__h { font-weight: 700; font-size: 1rem; margin-bottom: 14px; color: #fff; }
        .cnt-why__row {
          display: flex; align-items: flex-start; gap: 10px;
          color: #cbd5e1; font-size: .9rem; margin-bottom: 10px;
        }
        .cnt-why__row:last-child { margin-bottom: 0; }
        .cnt-tick {
          flex-shrink: 0; margin-top: 3px;
          width: 19px; height: 19px; border-radius: 50%;
          display: grid; place-items: center; font-size: .52rem; color: #fff;
          background: linear-gradient(135deg, var(--peach-deep), var(--peach));
        }

        /* --- Form --- */
        .cnt-form {
          background: #fff; border-radius: 24px;
          padding: clamp(26px, 3.2vw, 40px);
          border: 1px solid #f2e6df;
          box-shadow: 0 20px 46px rgba(194,65,12,.12);
        }
        .cnt-form__h {
          margin: 18px 0 24px; color: #0f172a; font-weight: 800;
          font-size: clamp(1.4rem, 2.6vw, 1.9rem); letter-spacing: -.02em;
        }
        .cnt-form .form-label {
          font-weight: 600; font-size: .86rem; color: #334155; margin-bottom: 6px;
        }
        .cnt-form .form-control, .cnt-form .form-select {
          border: 1px solid #e6dbd4; border-radius: 12px;
          padding: 11px 14px; font-size: .95rem; background: #fffdfc;
          transition: border-color .2s ease, box-shadow .2s ease;
        }
        .cnt-form .form-control:focus, .cnt-form .form-select:focus {
          border-color: var(--peach);
          box-shadow: 0 0 0 4px rgba(255,144,102,.22);
        }
        .cnt-form .form-control::placeholder { color: #b6a79f; }
        .cnt-submit {
          display: inline-flex; align-items: center; gap: 10px;
          background: linear-gradient(135deg, var(--peach-deep), var(--peach));
          border: none; border-radius: 14px; color: #0f172a; font-weight: 700;
          padding: 14px 30px; font-size: 1rem;
          box-shadow: 0 12px 28px rgba(255,107,53,.4);
          transition: transform .22s ease, box-shadow .22s ease, opacity .2s ease;
        }
        .cnt-submit:hover:not(:disabled) {
          transform: translateY(-3px); box-shadow: 0 18px 38px rgba(255,107,53,.55);
        }
        .cnt-submit:disabled { opacity: .65; cursor: not-allowed; }
        .cnt-submit svg { transition: transform .22s ease; }
        .cnt-submit:hover:not(:disabled) svg { transform: translateX(4px); }
        @media (max-width: 575.98px) { .cnt-submit { width: 100%; justify-content: center; } }

        @media (prefers-reduced-motion: reduce) {
          .cnt-submit, .cnt-detail__icon { transition: none; }
          .cnt-submit:hover { transform: none; }
        }
      `}</style>

      <div className="cnt">
        {/* Hero */}
        <section className="cnt-hero">
          <span className="cnt-hero__blob" />
          <Container className="text-center" style={{ position: "relative", zIndex: 1 }}>
            <span className="cnt-badge" data-aos="fade-up">
              <FaPaperPlane />
              CONTACT US
            </span>

            {/* theme.css hides <br> in section headings below 576px — the
                explicit space stops the halves concatenating. */}
            <h1 className="cnt-hero__title" data-aos="fade-up" data-aos-delay="60">
              Let's Transform
              <br />{" "}
              <em>Your Business Together</em>
            </h1>

            <p className="cnt-hero__lead" data-aos="fade-up" data-aos-delay="120">
              Connect with our consultants to discuss project delivery,
              enterprise technology, digital transformation and business growth
              opportunities.
            </p>
          </Container>
        </section>

        {/* Info + form */}
        <section className="cnt-body">
          <Container>
            <Row className="g-4 g-lg-5">
              <Col lg={5} data-aos="fade-right">
                <div className="cnt-info">
                  <h2 className="cnt-info__h">Get In Touch</h2>
                  <p className="cnt-info__p">
                    Whether you're planning a transformation programme,
                    implementing enterprise technology, or looking to accelerate
                    growth, our experts are ready to help.
                  </p>

                  {DETAILS.map(({ Icon, label, value, href }) => {
                    const inner = (
                      <>
                        <span className="cnt-detail__icon">
                          <Icon />
                        </span>
                        <span>
                          <span className="cnt-detail__l">{label}</span>
                          <span className="cnt-detail__v">{value}</span>
                        </span>
                      </>
                    );
                    // Email and phone are actionable; the rest are plain text.
                    return href ? (
                      <a className="cnt-detail" href={href} key={label}>
                        {inner}
                      </a>
                    ) : (
                      <div className="cnt-detail" key={label}>
                        {inner}
                      </div>
                    );
                  })}

                  <div className="cnt-why">
                    <h3 className="cnt-why__h">Why Work With Sarvavya?</h3>
                    {REASONS.map((r) => (
                      <div className="cnt-why__row" key={r}>
                        <span className="cnt-tick">
                          <FaCheck />
                        </span>
                        {r}
                      </div>
                    ))}
                  </div>
                </div>
              </Col>

              <Col lg={7} data-aos="fade-left">
                <div className="cnt-form">
                  <span className="cnt-badge">CONTACT FORM</span>
                  <h2 className="cnt-form__h">Send Us A Message</h2>

                  {/* aria-live so the outcome is announced, not just shown */}
                  <div aria-live="polite">
                    {status.message && (
                      <Alert variant={status.type}>{status.message}</Alert>
                    )}
                  </div>

                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3" controlId="cf-name">
                          <Form.Label>Full Name</Form.Label>
                          <Form.Control
                            type="text"
                            name="name"
                            autoComplete="name"
                            placeholder="Enter your name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3" controlId="cf-company">
                          <Form.Label>Company</Form.Label>
                          <Form.Control
                            type="text"
                            name="company"
                            autoComplete="organization"
                            placeholder="Company name"
                            value={formData.company}
                            onChange={handleChange}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3" controlId="cf-email">
                          <Form.Label>Email Address</Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            autoComplete="email"
                            placeholder="Enter your email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3" controlId="cf-phone">
                          <Form.Label>Phone Number</Form.Label>
                          <Form.Control
                            type="tel"
                            name="phone"
                            autoComplete="tel"
                            placeholder="Enter your phone number"
                            value={formData.phone}
                            onChange={handleChange}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3" controlId="cf-service">
                      <Form.Label>Service Required</Form.Label>
                      {/* Sourced from the services data so it can't drift */}
                      <Form.Select
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                      >
                        <option value="">Select Service</option>
                        {services.map((s) => (
                          <option key={s.slug}>{s.title}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-4" controlId="cf-message">
                      <Form.Label>Project Details</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={5}
                        name="message"
                        placeholder="Tell us about your project or requirements…"
                        value={formData.message}
                        onChange={handleChange}
                      />
                    </Form.Group>

                    <button type="submit" className="cnt-submit" disabled={submitting}>
                      {submitting ? "Sending…" : "Send Message"}
                      {!submitting && <FaArrowRight />}
                    </button>
                  </Form>
                </div>
              </Col>
            </Row>
          </Container>
        </section>
      </div>

      <Footer />
    </>
  );
}

export default Contact;
